import { useRef, useEffect } from 'react';

interface PanZoomOptions {
    minScale?: number;
    maxScale?: number;
    enabled?: boolean;
    onTap?: (e: PointerEvent) => void;
}

export function usePanZoom(
    containerRef: React.RefObject<HTMLDivElement | null>,
    targetRef: React.RefObject<HTMLDivElement | null>,
    options: PanZoomOptions = {}
) {
    const { minScale = 1, maxScale = 2, onTap, enabled = true } = options;
    const transform = useRef({ x: 0, y: 0, scale: 1 });
    const TAP_THRESHOLD = 5;

    const applyTransform = (ref: HTMLDivElement) => {
        const { x, y, scale } = transform.current;
        ref.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    };

    const clamp = (x: number, y: number, scale: number) => {
        if (!containerRef.current || !targetRef.current) return { x, y };
        const vw = containerRef.current.offsetWidth;
        const vh = containerRef.current.offsetHeight;
        const mw = targetRef.current.offsetWidth * scale;
        const mh = targetRef.current.offsetHeight * scale;
        return {
            x: Math.min(0, Math.max(vw - mw, x)),
            y: Math.min(0, Math.max(vh - mh, y)),
        };
    };

    useEffect(() => {
        if (!enabled) return; // ← додай це
        const container = containerRef.current;
        const target = targetRef.current;
        if (!container || !target) return;

        // --- DRAG ---
        let isDragging = false;
        let startX = 0, startY = 0;
        let dragStartX = 0, dragStartY = 0;

        const onPointerDown = (e: PointerEvent) => {
            if (e.pointerType === 'touch' && e.isPrimary === false) return;
            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            dragStartX = transform.current.x;
            dragStartY = transform.current.y;
            container.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isDragging) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            const clamped = clamp(dragStartX + dx, dragStartY + dy, transform.current.scale);
            transform.current.x = clamped.x;
            transform.current.y = clamped.y;
            applyTransform(target);
        };

        const onPointerUp = (e: PointerEvent) => {
            if (!isDragging) return;
            isDragging = false;
            const dx = Math.abs(e.clientX - startX);
            const dy = Math.abs(e.clientY - startY);
            if (dx < TAP_THRESHOLD && dy < TAP_THRESHOLD) {
                onTap?.(e);
            }
        };

        // --- WHEEL ---
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const { x, y, scale } = transform.current;
            const newScale = Math.min(maxScale, Math.max(minScale, scale - e.deltaY * 0.001));
            const rect = container.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const ratio = newScale / scale;
            const clamped = clamp(mx - ratio * (mx - x), my - ratio * (my - y), newScale);
            transform.current = { ...clamped, scale: newScale };
            applyTransform(target);
        };

        // --- PINCH ---
        let lastDist = 0;
        let lastMidX = 0, lastMidY = 0;

        const getTouches = (e: TouchEvent) => {
            const t0 = e.touches[0]!;
            const t1 = e.touches[1]!;
            const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
            const midX = (t0.clientX + t1.clientX) / 2;
            const midY = (t0.clientY + t1.clientY) / 2;
            return { dist, midX, midY };
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                isDragging = false;
                const { dist, midX, midY } = getTouches(e);
                lastDist = dist;
                lastMidX = midX;
                lastMidY = midY;
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;
            e.preventDefault();
            const { dist, midX, midY } = getTouches(e);
            const { x, y, scale } = transform.current;
            const newScale = Math.min(maxScale, Math.max(minScale, scale * (dist / lastDist)));
            const rect = container.getBoundingClientRect();
            const mx = midX - rect.left;
            const my = midY - rect.top;
            const ratio = newScale / scale;
            // пан + зум одночасно
            const panDx = midX - lastMidX;
            const panDy = midY - lastMidY;
            const clamped = clamp(
                mx - ratio * (mx - x) + panDx,
                my - ratio * (my - y) + panDy,
                newScale
            );
            transform.current = { ...clamped, scale: newScale };
            applyTransform(target);
            lastDist = dist;
            lastMidX = midX;
            lastMidY = midY;
        };

        container.addEventListener('pointerdown', onPointerDown);
        container.addEventListener('pointermove', onPointerMove);
        container.addEventListener('pointerup', onPointerUp);
        container.addEventListener('wheel', onWheel, { passive: false });
        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false });

        return () => {
            container.removeEventListener('pointerdown', onPointerDown);
            container.removeEventListener('pointermove', onPointerMove);
            container.removeEventListener('pointerup', onPointerUp);
            container.removeEventListener('wheel', onWheel);
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
        };
    }, [minScale, maxScale, enabled]);

    return { transform };
}